import moment from 'moment';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FaEye } from 'react-icons/fa';
import { FaFilePdf } from 'react-icons/fa6';
import { IoCheckmarkDone } from 'react-icons/io5';
import { MdCancel, MdDevices } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import swal from 'sweetalert';
import { FiltroAcciones } from '../../../Components/Acciones/FiltroAcciones';
import { Encabezado } from '../../../Components/Encabezado/Encabezado';
import Loading from '../../../Components/Modals/Loading/Loader';

import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';

import {
  fetchCambiarSuccess,
  fetchGenerarReporteAccionPDF,
  fetchGetAccionesReclutamiento,
  fetchGetAccionesReclutamientoCount,
  fetchProcesarAccionReclutamiento,
  fetchSolicitudAccionesEquiposInsumos,
  selectCountAccionesReclutamiento,
  selectIsLoading,
  selectIsSuccess,
  selectListaAccionesReclutamiento,
  selectMessage,
} from '../../../redux/Acciones/AccionesSlice';

import { CancelarAccionModal } from '../../../Components/Acciones/Modal/CancelarAccionModal';
import { SolicitudEquiposModal } from '../../../Components/Acciones/Modal/SolicitudEquiposModal';
import { selectUserData } from '../../../redux/Login/LoginSlice';

const solicitudDefaultValues = {
  usuario: false,
  correo: false,
  transdoc: false,
  pc: false,
  impresora: false,
  flota: false,
  telefonoFijo: false,
};

export const ReclutamientoSeleccion = () => {
  // ? HOOK-DISPATCH
  const dispatch = useDispatch();

  // ? HOOK-NAVEGACION
  const navigate = useNavigate();

  // ? HOOK-STATE
  const [open, setOpen] = useState(false);
  const [openSolicitud, setOpenSolicitud] = useState(false);
  const [accion, setAccion] = useState({});

  // ? HOOK-SELECTOR
  const listaAccionesReclutamiento = useSelector(selectListaAccionesReclutamiento);
  const countAccionesReclutamiento = useSelector(selectCountAccionesReclutamiento);
  const isLoading = useSelector(selectIsLoading);
  const usuario = useSelector(selectUserData);
  const message = useSelector(selectMessage);
  const isSuccess = useSelector(selectIsSuccess);

  // ? HOOK-FORM
  const {
    register,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      empleado: {
        areaId: 0,
        empleadoId: 0,
        estadoId: 2,
      },
    },
  });

  // ? HOOK-FORM
  const formSolicitud = useForm({
    defaultValues: {
      accion: {},
      solicitud: solicitudDefaultValues,
      noAplica: false,
    },
  });

  // ? HOOK-WATCH
  const empleado = useWatch({ control, name: 'empleado' });

  // ? USE-STATE PARA LA PAGINACION
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [skipRow, setSkipRow] = useState(0);

  // * FUNCIONES PARA PAGINACION.
  const handleChangePage = (event, newPage) => {
    setSkipRow(newPage * rowsPerPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSkipRow(0);
  };

  // * FUNCION PARA REFRESCAR LISTA.
  const refreshList = () => dispatch(fetchGetAccionesReclutamiento({ skipRow, rowsPerPage, empleado }));

  // * FUNCION PARA REFRESCAR SUCCESS
  const refreshSuccess = () => dispatch(fetchCambiarSuccess());

  // ? HOOK-EFFECT
  useEffect(() => {
    refreshList();
  }, [empleado, page, rowsPerPage]);

  // ? HOOK-EFFECT PARA LAS ALERTAS
  useEffect(() => {
    if (isSuccess === 'success' && message.length > 0) {
      setOpen(false);
      setOpenSolicitud(false);
      formSolicitud.reset();
      swal({ title: 'Exitoso', text: message, icon: 'success' });
      refreshList();
      refreshSuccess();
    } else if (isSuccess === 'error') {
      swal({ title: 'Error', text: message, icon: 'error' });
      refreshSuccess();
    }
  }, [isSuccess]);

  // ? HOOK-EFFECT PARA OBTENER LA CANTIDAD DE ACCIONES DE RECLUTAMIENTO
  // ? DESDE EL BACK-END
  useEffect(() => {
    dispatch(fetchGetAccionesReclutamientoCount(empleado));
  }, [empleado]);

  // * FUNCION PARA PROCESAR UNA ACCION
  const procesarAccion = (accionId) => {
    // // * VALIDAR DE QUE EXISTA AL MENOS UNA SELECCION DE EQUIPO.
    // const validacionOpcionesMarcadas = Object.values(solicitud).some((value) => value);

    // if (!validacionOpcionesMarcadas && !noAplica) {
    //   swal('Error!', 'Debe seleccionar al menos una opción. Si ninguna aplica, pulse el botón “No Aplica”.', 'error');
    //   return;
    // }

    swal({
      title: 'Procesar acción',
      text: 'Estás seguro/a de procesar esta acción?',
      icon: 'warning',
      buttons: ['No', 'Si'],
    }).then((respuesta) => {
      const data = {
        accionId,
        usuarioIdModificado: usuario.Id,
      };

      if (respuesta) {
        dispatch(fetchProcesarAccionReclutamiento(data));
      }
    });
  };

  // * FUNCION PARA SOLICITAR EQUIPOS E INSUMOS.
  const solicitudEquiposInsumos = ({ solicitud, noAplica, accion }) => {
    // * VALIDAR DE QUE EXISTA AL MENOS UNA SELECCION DE EQUIPO.
    const validacionOpcionesMarcadas = Object.values(solicitud).some((value) => value);

    if (!validacionOpcionesMarcadas && !noAplica) {
      swal('Error!', 'Debe seleccionar al menos una opción. Si ninguna aplica, pulse el botón “No Aplica”.', 'error');
      return;
    }

    swal({
      title: `Solicitud de Equipos e Insumos a ${accion.empleado.nombreLabel}`,
      text: 'Estás seguro/a de realizar una solicitud de equipos e insumos?',
      icon: 'warning',
      buttons: ['No', 'Si'],
    }).then((respuesta) => {
      const data = {
        accionId: accion.accionId,
        ...solicitud,
      };

      if (respuesta) {
        dispatch(fetchSolicitudAccionesEquiposInsumos(data));
      }
    });
  };

  // * FUNCION PARA CANCELAR UNA ACCION
  const cancelarAccion = (accion) => {
    setOpen(true);
    setAccion(accion);
  };

  // * FUNCION PARA GENERAR REPORTE
  const generarReportePDF = (accion) => dispatch(fetchGenerarReporteAccionPDF(accion));

  return (
    <Encabezado title="Acciones de Reclutamiento">
      <FiltroAcciones
        control={control}
        register={register}
        reset={reset}
        rutaAccionBotonAgregar={'/acciones/reclutamiento/agregar'}
        permisoBoton={'ROLE_RECLUTAMIENTO_ACCIONES'}
        filtroAccionesSinEfecto={true}
      />

      <Box sx={{ mt: 3 }} maxWidth={'100%'}>
        <Paper sx={{ mb: 2 }} elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Fecha de Creación</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Fecha de Efectividad</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Empleado</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Código</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Área Organizacional</TableCell>
                  <TableCell align="center" style={{ fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {listaAccionesReclutamiento.length === 0 ? (
                  <TableCell colSpan={6} align="center">
                    <Typography>No hay registros</Typography>
                  </TableCell>
                ) : (
                  listaAccionesReclutamiento.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{moment(item?.fechaCreacion).format('DD/MM/yyyy')}</TableCell>

                      <TableCell>{moment(item?.fechaEfectividad).format('DD/MM/yyyy')}</TableCell>

                      <TableCell>{item.empleado.nombreLabel}</TableCell>

                      <TableCell>{item.empleado.codigoEmpleado}</TableCell>

                      <TableCell>{item.empleado.areaOrganizacional.nombre}</TableCell>

                      <TableCell align="center">
                        {/* SI EL ESTADO ES "SIN EFECTO" SE OCULTARA LOS SIGUIENTES BOTONES. */}
                        <IconButton
                          title="Procesar Acción"
                          id="btn-procesar-accion"
                          hidden={item.estadoId !== 2}
                          onClick={() => procesarAccion(item.accionId)}
                        >
                          <IoCheckmarkDone color="#005215" />
                        </IconButton>

                        <IconButton
                          title="Solicitud Equipos e Insumos"
                          id="btn-solicitar-equipos"
                          hidden={item.estadoId !== 2}
                          onClick={() => {
                            if (item.solicitudEquipos) {
                              const solicitudEquiposForm = (({ accionId, ...resto }) => resto)(item.solicitudEquipos);

                              formSolicitud.setValue('solicitud', solicitudEquiposForm);
                              formSolicitud.setValue('noAplica', !Object.values(solicitudEquiposForm).some((value) => value));
                            }

                            formSolicitud.setValue('accion', item);
                            setOpenSolicitud(true);
                          }}
                        >
                          <MdDevices color="#0464d3" />
                        </IconButton>

                        <IconButton
                          title="Cancelar Acción"
                          id="btn-cancelar-accion"
                          hidden={item.estadoId !== 2}
                          onClick={() => cancelarAccion(item)}
                        >
                          <MdCancel color="#BD0000" />
                        </IconButton>

                        <IconButton title="Generar Acción" id="btn-generar-pdf-accion" onClick={() => generarReportePDF(item)}>
                          <FaFilePdf color="#BD0000" />
                        </IconButton>

                        <IconButton
                          title="Ver Detalle"
                          id="btn-ver-detalle-accion"
                          onClick={() => navigate(`/acciones/reclutamiento/detalle/${item.accionId}`)}
                        >
                          <FaEye color="#003876" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={countAccionesReclutamiento}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      <Loading open={isLoading} />

      <CancelarAccionModal open={open} setOpen={setOpen} accion={accion} usuarioId={usuario?.Id} />

      <FormProvider {...formSolicitud}>
        <SolicitudEquiposModal
          solicitudDefaultValues={solicitudDefaultValues}
          open={openSolicitud}
          setOpen={setOpenSolicitud}
          solicitudEquiposInsumos={solicitudEquiposInsumos}
        />
      </FormProvider>
    </Encabezado>
  );
};
